import React, { useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import Axios from 'axios';
import { differenceBy } from 'lodash';
import { MoreHorizontal, Pencil, ExternalLink, X, Check, Loader2 } from 'lucide-react';
import { Link, useHistory } from 'react-router-dom';
import { Button } from '../../../../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../../../components/ui/dropdown-menu';
import { DepartmentIcon } from '../../../../../lib/core.cjs.development';
import { cn } from '../../../../../lib/utils';
import apiService from '../../../../../services/api.service';
import ConfirmChangesDialog from './ConfirmChangesDialog';

function DepartmentRow({ department, onSuccess, onError }) {
  const history = useHistory();
  const cancelToken = useRef(null);

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  // Form state
  const [departmentName, setDepartmentName] = useState(department.title);
  const [currentHeads, setCurrentHeads] = useState(
    department.heads.map((head) => ({ id: head._id, email: head.email })),
  );
  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  // Original values for comparison
  const [originalName] = useState(department.title);
  const [originalHeads] = useState(
    department.heads.map((head) => ({ id: head._id, email: head.email })),
  );

  // Confirmation dialog
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingChanges, setPendingChanges] = useState(null);

  const handleSearch = useCallback(
    async (query) => {
      if (query.length < 3) {
        setSearchResults([]);
        return;
      }

      if (cancelToken.current) {
        cancelToken.current.cancel('Operation canceled due to new request.');
      }

      cancelToken.current = Axios.CancelToken.source();

      try {
        setIsSearching(true);
        const result = await apiService.admin.getUsers({
          params: { 'filter[email]': query },
          cancelToken: cancelToken.current.token,
        });

        const filteredResults = result.paginatedResult
          .map((item) => ({ id: item._id, email: item.email }))
          .filter((item) => !currentHeads.some((head) => head.id === item.id));

        setSearchResults(filteredResults);
      } catch (error) {
        if (!Axios.isCancel(error)) {
          console.error('Search error:', error);
        }
      } finally {
        setIsSearching(false);
      }
    },
    [currentHeads],
  );

  const handleAddHead = (user) => {
    setCurrentHeads([...currentHeads, user]);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleRemoveHead = (userId) => {
    setCurrentHeads(currentHeads.filter((head) => head.id !== userId));
  };

  const handleCancel = () => {
    setDepartmentName(originalName);
    setCurrentHeads(originalHeads);
    setIsEditing(false);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleSave = () => {
    if (!departmentName.trim()) {
      onError('Department name is required!');
      return;
    }

    const addedHeads = differenceBy(currentHeads, originalHeads, 'id');
    const removedHeads = differenceBy(originalHeads, currentHeads, 'id');
    const nameChanged = originalName !== departmentName;

    if (nameChanged || addedHeads.length > 0 || removedHeads.length > 0) {
      setPendingChanges({
        nameChanged: nameChanged ? { from: originalName, to: departmentName } : null,
        addedHeads: addedHeads.length > 0 ? addedHeads : null,
        removedHeads: removedHeads.length > 0 ? removedHeads : null,
      });
      setShowConfirmDialog(true);
    } else {
      setIsEditing(false);
    }
  };

  const handleConfirm = async () => {
    try {
      setIsSaving(true);
      await apiService.admin.updateDepartment(department._id, {
        title: departmentName,
        headIds: currentHeads.map((head) => head.id),
      });

      setShowConfirmDialog(false);
      setIsEditing(false);
      onSuccess('Department updated successfully!');
    } catch (error) {
      console.error('Save error:', error);
      onError(error.message || 'Failed to update department');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDecline = () => {
    setShowConfirmDialog(false);
    setPendingChanges(null);
  };

  const handleGrowClick = () => {
    history.push(`/head/departments/${department.name}/grow`);
  };

  return (
    <>
      <div className="flex items-center gap-4 px-4 py-3 hover:bg-muted/30 transition-colors">
        {/* Department Icon */}
        <div className="w-16">
          <DepartmentIcon name={department.name} style={{ width: '40px', height: '40px' }} />
        </div>

        {/* Department Name */}
        <div className="w-40">
          {isEditing ? (
            <input
              type="text"
              value={departmentName}
              onChange={(e) => setDepartmentName(e.target.value)}
              className={cn(
                'w-full px-2 py-1 rounded border bg-background text-sm',
                'focus:outline-none focus:ring-2 focus:ring-ring',
              )}
              style={{ borderColor: 'hsl(var(--border))' }}
            />
          ) : (
            <span className="font-medium text-sm">{departmentName}</span>
          )}
        </div>

        {/* Heads */}
        <div className="flex-1 min-w-0">
          {isEditing ? (
            <div className="space-y-2">
              {/* Current Heads */}
              <div className="flex flex-wrap gap-1">
                {currentHeads.map((head) => (
                  <span
                    key={head.id}
                    className={cn(
                      'inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs',
                      'bg-primary/10 text-primary',
                    )}
                  >
                    {head.email}
                    <button
                      type="button"
                      onClick={() => handleRemoveHead(head.id)}
                      className="hover:bg-primary/20 rounded-full p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>

              {/* Search Input */}
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    handleSearch(e.target.value);
                  }}
                  placeholder="Search users by email (min 3 chars)..."
                  className={cn(
                    'w-full max-w-xs px-2 py-1 rounded border bg-background text-xs',
                    'focus:outline-none focus:ring-2 focus:ring-ring',
                  )}
                  style={{ borderColor: 'hsl(var(--border))' }}
                />
                {isSearching && (
                  <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 h-3 w-3 animate-spin text-muted-foreground" />
                )}

                {/* Search Results Dropdown */}
                {searchResults.length > 0 && (
                  <div
                    className="absolute z-10 mt-1 w-full max-w-xs bg-popover border rounded-md shadow-lg"
                    style={{ borderColor: 'hsl(var(--border))' }}
                  >
                    {searchResults.map((user) => (
                      <button
                        key={user.id}
                        type="button"
                        onClick={() => handleAddHead(user)}
                        className="w-full px-3 py-2 text-left text-xs hover:bg-muted transition-colors"
                      >
                        {user.email}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap gap-1">
              {currentHeads.length > 0 ? (
                currentHeads.map((head) => (
                  <span
                    key={head.id}
                    className="px-2 py-1 rounded-full text-xs bg-muted text-muted-foreground"
                  >
                    {head.email}
                  </span>
                ))
              ) : (
                <span className="text-xs text-muted-foreground italic">No heads assigned</span>
              )}
            </div>
          )}
        </div>

        {/* Employee Count */}
        <div className="w-28 text-right">
          <Link
            to={`/admin/users?${department._id}`}
            className="text-sm font-medium text-primary hover:underline"
          >
            {department.employeesCount}
          </Link>
        </div>

        {/* Actions */}
        <div className="w-20 flex justify-end">
          {isEditing ? (
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" onClick={handleCancel} disabled={isSaving}>
                <X className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleSave}
                disabled={isSaving}
                className="text-green-600 hover:text-green-700"
              >
                {isSaving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Check className="h-4 w-4" />
                )}
              </Button>
            </div>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setIsEditing(true)}>
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleGrowClick}>
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Grow
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* Confirmation Dialog */}
      <ConfirmChangesDialog
        isOpen={showConfirmDialog}
        changes={pendingChanges}
        onConfirm={handleConfirm}
        onCancel={handleDecline}
        isSaving={isSaving}
      />
    </>
  );
}

DepartmentRow.propTypes = {
  department: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    employeesCount: PropTypes.number.isRequired,
    heads: PropTypes.arrayOf(
      PropTypes.shape({
        _id: PropTypes.string.isRequired,
        email: PropTypes.string.isRequired,
      }),
    ).isRequired,
  }).isRequired,
  onSuccess: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};

export default DepartmentRow;
